import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Linkedin } from 'lucide-react'
import type { RecapIntegrationItem, RecapResponse } from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { onboardingClient } from '@/lib/api/onboarding.client'
import { windowPonyfill } from '@/lib/utils/window-ponyfill'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep } from '../flow.config'

const FAST_SETUP_THRESHOLD_MS = 5 * 60 * 1000
const SUPER_URL = 'https://hiresuper.com'

function formatPhone(raw: string | null): string {
  if (!raw) return '—'
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

const BILLING_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Incomplete',
  unpaid: 'Unpaid',
}

const MUTED = 'text-muted-foreground'
const WARN = 'text-amber-600 dark:text-amber-400'

function IntegrationRowValue({ item }: { item: RecapIntegrationItem }) {
  if (item.status === 'synced' && item.recordsProcessed != null) {
    return (
      <span>Synced · {item.recordsProcessed.toLocaleString()} records</span>
    )
  }
  if (item.status === 'synced') return <span>Synced</span>
  if (item.status === 'error') return <span className={WARN}>Sync error</span>
  return <span className={MUTED}>Connected</span>
}

function BillingRowValue({
  status,
  tierLabel,
}: {
  status: string | null
  tierLabel: string | null
}) {
  if (!status) return <span className={MUTED}>Not set</span>
  const statusLabel = BILLING_STATUS_LABEL[status] ?? status
  const label = tierLabel ? `${tierLabel} · ${statusLabel}` : statusLabel
  const tone = status === 'past_due' || status === 'unpaid' ? WARN : ''
  return <span className={tone}>{label}</span>
}

function ReceiptRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="border-border/50 flex items-center justify-between gap-6 border-b py-3 last:border-b-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="text-foreground text-right text-sm font-medium tabular-nums">
        {value}
      </span>
    </div>
  )
}

function Receipt({ recap }: { recap: RecapResponse }) {
  return (
    <section className="border-border rounded-lg border p-6">
      <header className="border-border/50 mb-2 border-b pb-4 text-center">
        <p className="font-space-grotesk text-2xl font-bold tracking-tight">
          Setup checklist
        </p>
      </header>
      <div>
        <ReceiptRow
          label="Number"
          value={
            recap.phoneNumber ? (
              formatPhone(recap.phoneNumber)
            ) : (
              <span className={MUTED}>Not set</span>
            )
          }
        />
        {recap.integrations.length === 0 ? (
          <ReceiptRow
            label="Integrations"
            value={<span className={MUTED}>None connected</span>}
          />
        ) : (
          recap.integrations.map(item => (
            <ReceiptRow
              key={item.type}
              label={item.label}
              value={<IntegrationRowValue item={item} />}
            />
          ))
        )}
        <ReceiptRow
          label="Billing"
          value={
            <BillingRowValue
              status={recap.billingStatus}
              tierLabel={recap.billingTierLabel}
            />
          }
        />
      </div>
    </section>
  )
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000))
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  if (min === 0) return `${sec} sec`
  return `${min} min ${sec} sec`
}

function shareText(recap: RecapResponse, isFast: boolean): string {
  if (isFast && recap.setupDurationMs != null) {
    const duration = formatDuration(recap.setupDurationMs)
    return `${duration} to get through the Super setup process. Easiest way to get AI voice agents for property management set up. ${SUPER_URL}`
  }
  return `Just finished my Super setup. Anyone have any agent flows they want to share? ${SUPER_URL}`
}

function openLinkedInShare(text: string) {
  const url =
    `https://www.linkedin.com/feed/?shareActive=true` +
    `&shareUrl=${encodeURIComponent(SUPER_URL)}` +
    `&text=${encodeURIComponent(text)}`
  windowPonyfill.open(url, '_blank', 'noopener,noreferrer')
}

function ShareCard({ recap }: { recap: RecapResponse }) {
  const isFast =
    recap.setupDurationMs != null &&
    recap.setupDurationMs < FAST_SETUP_THRESHOLD_MS
  const text = shareText(recap, isFast)

  const onShare = () => openLinkedInShare(text)

  return isFast ? (
    <FastShareCard recap={recap} text={text} onShare={onShare} />
  ) : (
    <GenericShareCard text={text} onShare={onShare} />
  )
}

interface CardBodyProps {
  text: string
  onShare: () => void
}

function FastShareCard({
  recap,
  text,
  onShare,
}: CardBodyProps & { recap: RecapResponse }) {
  const duration = formatDuration(recap.setupDurationMs ?? 0)
  return (
    <div className="border-border bg-card w-full overflow-hidden rounded-xl border shadow-sm">
      <div className="bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent px-6 py-8 text-center">
        <p className="text-emerald-600 dark:text-emerald-400 font-fragment-mono text-[10px] font-medium uppercase tracking-[0.18em]">
          Setup time
        </p>
        <p className="font-space-grotesk mt-3 text-5xl font-semibold tabular-nums tracking-tight">
          {duration}
        </p>
        <p className="text-muted-foreground mt-3 text-sm">
          You beat the 5-minute mark. Tell the world.
        </p>
      </div>
      <div className="border-border/60 border-t px-6 py-5">
        <p className="text-muted-foreground font-fragment-mono mb-2 text-[10px] uppercase tracking-[0.14em]">
          You'll post this
        </p>
        <p className="text-foreground/90 whitespace-pre-line text-sm leading-relaxed">
          {text}
        </p>
      </div>
      <CardActions onShare={onShare} />
    </div>
  )
}

function GenericShareCard({ text, onShare }: CardBodyProps) {
  return (
    <div className="border-border bg-card w-full overflow-hidden rounded-xl border shadow-sm">
      <div className="bg-gradient-to-br from-blue-500/10 via-transparent to-transparent px-6 py-8 text-center">
        <p className="text-blue-600 dark:text-blue-400 font-fragment-mono text-[10px] font-medium uppercase tracking-[0.18em]">
          Now live
        </p>
        <p className="font-space-grotesk mt-3 text-3xl font-semibold tracking-tight">
          Share your progress
        </p>
        <p className="text-muted-foreground mt-3 text-sm">
          Find who else is building on Super.
        </p>
      </div>
      <div className="border-border/60 border-t px-6 py-5">
        <p className="text-muted-foreground font-fragment-mono mb-2 text-[10px] uppercase tracking-[0.14em]">
          You'll post this
        </p>
        <p className="text-foreground/90 whitespace-pre-line text-sm leading-relaxed">
          {text}
        </p>
      </div>
      <CardActions onShare={onShare} />
    </div>
  )
}

function CardActions({ onShare }: { onShare: () => void }) {
  return (
    <div className="border-border/60 border-t px-6 py-4">
      <Button size="sm" onClick={onShare} className="w-full">
        <Linkedin className="mr-2 h-4 w-4" />
        Share on LinkedIn
      </Button>
    </div>
  )
}

export function RecapStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = useOnboarding()

  const [recap, setRecap] = useState<RecapResponse | null | undefined>(
    undefined,
  )

  useEffect(() => {
    if (state.currentStepId !== 'recap') return
    let cancelled = false
    onboardingClient
      .getRecap()
      .then(r => {
        if (!cancelled) setRecap(r)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load recap', err)
        setRecap(null)
      })
    return () => {
      cancelled = true
    }
  }, [state.currentStepId])

  const advance = () => {
    const current = findStepByRoute(location.pathname)
    const next = current ? nextStep(current, state.userType ?? null) : null
    if (next) navigate(next.route)
  }

  return (
    <div className="mx-auto flex w-full max-w-[520px] flex-col gap-6">
      <div className="space-y-2 text-center">
        <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
          You're set up
        </h1>
        <p className="text-muted-foreground text-sm">
          Welcome to the workflows of the future.
        </p>
      </div>

      {recap === undefined ? (
        <ShareCardSkeleton />
      ) : recap === null ? (
        <div className="border-border bg-card rounded-xl border p-6 text-center">
          <p className="text-muted-foreground text-sm">
            Couldn't load your recap. You can still continue.
          </p>
        </div>
      ) : (
        <>
          <Receipt recap={recap} />
          <ShareCard recap={recap} />
        </>
      )}

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-recap"
        priority={10}
      >
        <div className="pointer-events-auto">
          <Button onClick={advance} size="sm">
            Continue →
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}

function ShareCardSkeleton() {
  return (
    <div className="border-border bg-card overflow-hidden rounded-xl border">
      <div className="space-y-3 px-6 py-8 text-center">
        <div className="bg-muted mx-auto h-3 w-16 animate-pulse rounded" />
        <div className="bg-muted mx-auto h-8 w-40 animate-pulse rounded" />
        <div className="bg-muted mx-auto h-3 w-52 animate-pulse rounded" />
      </div>
      <div className="border-border/60 space-y-2 border-t px-6 py-5">
        <div className="bg-muted h-3 w-full animate-pulse rounded" />
        <div className="bg-muted h-3 w-3/4 animate-pulse rounded" />
      </div>
    </div>
  )
}
